import * as React from "react";
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  TablePagination,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import CodeIcon from "@mui/icons-material/Code";
import { useAuth0 } from "@auth0/auth0-react";

const API_BASE_URL =
  import.meta.env.VITE_API_BASE_URL || "http://localhost:8787";

export default function SubscriptionsList({
  subscriptions,
  loading,
  error,
  onRefresh,
  onViewAllJson,
  onViewSubscriptionDetails,
  formatDate,
}) {
  const { getAccessTokenSilently } = useAuth0();
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const [expandedId, setExpandedId] = React.useState(null);
  const [expandedData, setExpandedData] = React.useState(null);
  const [expandedLoading, setExpandedLoading] = React.useState(false);

  const handleChangePage = (_, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  const paginatedSubscriptions = subscriptions.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const fetchSubscriptionDetails = React.useCallback(async (subscriptionId) => {
    setExpandedLoading(true);
    setExpandedData(null);
    try {
      const token = await getAccessTokenSilently().catch(() => null);
      const response = await fetch(
        `${API_BASE_URL}/admin/squarespace/subscriptions/${encodeURIComponent(subscriptionId)}`,
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      const data = await response.json();
      if (response.ok) {
        setExpandedData(data.data || data);
      } else {
        setExpandedData({ error: data.message || data.error || "Failed to load" });
      }
    } catch (err) {
      setExpandedData({ error: err.message || "Failed to load" });
    } finally {
      setExpandedLoading(false);
    }
  }, [getAccessTokenSilently]);

  const handleRowClick = (sub) => {
    const id = sub?.id || sub?.subscriptionId;
    if (!id) return;
    if (expandedId === id) {
      setExpandedId(null);
      setExpandedData(null);
    } else {
      setExpandedId(id);
      fetchSubscriptionDetails(id);
    }
  };

  return (
    <>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Typography variant="h6" fontWeight="bold">
          Subscriptions
        </Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<RefreshIcon />}
            onClick={onRefresh}
            disabled={loading}
          >
            Refresh
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={<CodeIcon />}
            onClick={onViewAllJson}
          >
            View JSON
          </Button>
        </Box>
      </Box>
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : subscriptions.length === 0 ? (
        <Alert severity="info">No subscriptions found for this customer.</Alert>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Product</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Frequency</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Expires / IDs</TableCell>
                <TableCell>Auto Renew</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedSubscriptions.map((sub) => {
                const rowId = sub.id || sub.subscriptionId;
                const isExpanded = expandedId === rowId;
                const rowData = isExpanded ? expandedData : null;
                return (
                  <React.Fragment key={rowId}>
                    <TableRow
                      hover
                      onClick={() => handleRowClick(sub)}
                      sx={{ cursor: "pointer", "&:hover": { backgroundColor: "action.hover" } }}
                    >
                      <TableCell>{sub.productName || "—"}</TableCell>
                      <TableCell>{sub.productType || "—"}</TableCell>
                      <TableCell>{sub.frequency || "—"}</TableCell>
                      <TableCell>
                        <Chip
                          label={(sub.resoldStatus || "—").charAt(0).toUpperCase() + (sub.resoldStatus || "").slice(1).toLowerCase()}
                          size="small"
                          color={
                            sub.resoldStatus === "ACTIVE" ? "success" :
                            sub.resoldStatus === "CANCELLED" || sub.resoldStatus === "EXPIRED" ? "error" :
                            sub.resoldStatus === "PAUSED" ? "warning" : "default"
                          }
                        />
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2">{formatDate(sub.expiresOn)}</Typography>
                          <Typography variant="caption" display="block" fontFamily="monospace" sx={{ fontSize: "0.7rem", mt: 0.5 }}>
                            productId: {sub.productId || sub.planId || "—"}
                          </Typography>
                          <Typography variant="caption" display="block" fontFamily="monospace" sx={{ fontSize: "0.7rem" }}>
                            customerId: {sub.customerId || sub.customer_id || "—"}
                          </Typography>
                          <Typography variant="caption" display="block" fontFamily="monospace" sx={{ fontSize: "0.7rem" }}>
                            websiteId: {sub.websiteId || sub.website_id || "—"}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>{sub.autoRenew ? "Yes" : "No"}</TableCell>
                      <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                        {onViewSubscriptionDetails && (
                          <IconButton
                            size="small"
                            onClick={() => onViewSubscriptionDetails(sub)}
                            aria-label="View full subscription details"
                          >
                            <CodeIcon fontSize="small" />
                          </IconButton>
                        )}
                      </TableCell>
                    </TableRow>
                    {isExpanded && (
                      <TableRow>
                        <TableCell colSpan={7} sx={{ py: 2, backgroundColor: (theme) => theme.palette.mode === "dark" ? theme.palette.grey[900] : theme.palette.grey[50] }}>
                          {expandedLoading ? (
                            <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                              <CircularProgress size={24} />
                            </Box>
                          ) : rowData?.error ? (
                            <Alert severity="error">{rowData.error}</Alert>
                          ) : rowData ? (
                            <Box
                              component="pre"
                              sx={{
                                p: 1.5,
                                borderRadius: 1,
                                backgroundColor: (theme) => theme.palette.mode === "dark" ? theme.palette.grey[800] : theme.palette.grey[100],
                                fontSize: "0.75rem",
                                fontFamily: "monospace",
                                overflow: "auto",
                                maxHeight: 300,
                                margin: 0,
                              }}
                            >
                              {JSON.stringify(rowData, null, 2)}
                            </Box>
                          ) : null}
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={subscriptions.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25]}
            labelRowsPerPage="Rows:"
          />
        </TableContainer>
      )}
      <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
        Subscriptions for Squarespace are created via an order. Go to the <strong>Orders</strong> tab to create an order. When a Squarespace order is created, it is processed by Squarespace and creates a website.
      </Typography>
    </>
  );
}
